import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { CompaniesFacade } from '../../store/companies/companies.facade';
import { ReferenceFacade } from '../../store/reference/reference.facade';
import { Company, CompanyUpdateRequest } from '../../core';
import { CompanyFormComponent } from './company-form/company-form.component';
import {
  CHECK_LAYOUT_TYPE_TO_ID_MAP,
  CHECK_LAYOUT_ID_TO_TYPE_MAP,
} from './company-form.utils';

@Component({
  selector: 'app-edit-company',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CompanyFormComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './edit-company.component.html',
  styleUrls: ['./edit-company.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditCompanyComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly companiesFacade = inject(CompaniesFacade);
  private readonly referenceFacade = inject(ReferenceFacade);
  private readonly destroy$ = new Subject<void>();

  // Form
  companyForm!: FormGroup;

  // Company state
  companyId: string | null = null;
  selectedCustomerType = '';

  // Reactive state from facades
  readonly currentCompany$ = this.companiesFacade.currentCompany$;
  readonly loading$ = this.companiesFacade.currentCompanyLoading$;
  readonly error$ = this.companiesFacade.currentCompanyError$;
  readonly saving$ = this.companiesFacade.saving$;
  readonly saveSuccess$ = this.companiesFacade.saveSuccess$;
  readonly saveError$ = this.companiesFacade.saveError$;

  // Reference data from facade
  readonly printSites$ = this.referenceFacade.activePrintSites$;
  readonly customerTypes$ = this.referenceFacade.activeCustomerTypes$;

  ngOnInit(): void {
    this.initForm();
    this.loadReferenceData();
    this.loadCompanyFromRoute();
    this.subscribeToCompanyChanges();
  }

  ngOnDestroy(): void {
    this.companiesFacade.clearCurrentCompany();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.companyForm = this.fb.group({
      // Uneditable fields (company details)
      arNumber: [{ value: '', disabled: true }],
      name: [{ value: '', disabled: true }],

      // Editable fields
      customerTypeId: [null],
      preferPrintSiteId: [null],
      checkLayoutType: ['', Validators.required],
      backOfficeSort: [false],
      packingDetailsDistribution: [false],
      ultibank: [false],
      custom: [false],
      shippingOverwrite: [false],
      manualCheck: [false],
      groupRequests: [false],
      customProcessFileName: ['', [Validators.maxLength(255)]],
      comments: ['', [Validators.maxLength(2000)]],
    });
  }

  private loadReferenceData(): void {
    this.referenceFacade.loadAllReferenceData();
  }

  private loadCompanyFromRoute(): void {
    const arNumber = this.route.snapshot.paramMap.get('id');
    if (arNumber) {
      this.companyId = arNumber;
      this.companiesFacade.loadCompany(arNumber);
    } else {
      this.router.navigate(['/company']);
    }
  }

  private subscribeToCompanyChanges(): void {
    this.currentCompany$
      .pipe(
        filter((company): company is Company => company !== null),
        takeUntil(this.destroy$)
      )
      .subscribe(company => {
        this.populateForm(company);
        this.resolveCustomerTypeName(company.customerTypeId);
      });
  }

  private resolveCustomerTypeName(
    customerTypeId: number | null | undefined
  ): void {
    if (!customerTypeId) return;
    this.customerTypes$
      .pipe(
        filter(types => types.length > 0),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(types => {
        const match = types.find(t => t.customerTypeId === customerTypeId);
        this.selectedCustomerType = match?.description ?? '';
      });
  }

  private populateForm(company: Company): void {
    this.companyForm.patchValue({
      // Uneditable fields
      arNumber: company.arNumber,
      name: company.name,

      // Editable fields
      customerTypeId: company.customerTypeId ?? null,
      preferPrintSiteId: company.preferPrintSiteId ?? null,
      checkLayoutType:
        CHECK_LAYOUT_ID_TO_TYPE_MAP[company.checkLayoutId] ?? 'standard',
      backOfficeSort: company.isBackOfficeSort ?? false,
      packingDetailsDistribution: company.isPackingDetailDistribution ?? false,
      ultibank: company.isUltiBank ?? false,
      custom: company.isCustom ?? false,
      shippingOverwrite: company.isShippingUpCharge ?? false,
      manualCheck: company.isManualCheck ?? false,
      groupRequests: company.isGroupRequests ?? false,
      customProcessFileName: company.customProcessFileName ?? '',
      comments: company.comments ?? '',
    });
  }

  onSave(): void {
    if (this.companyForm.invalid) {
      this.companyForm.markAllAsTouched();
      return;
    }

    if (!this.companyId) {
      return;
    }

    const formValue = this.companyForm.getRawValue();

    const updateRequest: CompanyUpdateRequest = {
      customerTypeId: formValue.customerTypeId ?? null,
      preferPrintSiteId: formValue.preferPrintSiteId ?? 0,
      checkLayoutId:
        CHECK_LAYOUT_TYPE_TO_ID_MAP[formValue.checkLayoutType] ?? 1,
      isBackOfficeSort: formValue.backOfficeSort ?? false,
      isPackingDetailDistribution:
        formValue.packingDetailsDistribution ?? false,
      isUltiBank: formValue.ultibank ?? false,
      isCustom: formValue.custom ?? false,
      isShippingUpCharge: formValue.shippingOverwrite ?? false,
      isManualCheck: formValue.manualCheck ?? false,
      isGroupRequests: formValue.groupRequests ?? false,
      customProcessFileName: formValue.customProcessFileName ?? null,
      comments: formValue.comments ?? null,
    };

    this.companiesFacade.updateCompany(this.companyId, updateRequest);
  }

  onCancel(): void {
    this.router.navigate(['/company']);
  }

  // Check if form has unsaved changes
  get hasUnsavedChanges(): boolean {
    return this.companyForm.dirty;
  }
}
